import type { SelectOption } from "@/components/ui/Select";
import type { MedVisitCitation } from "@/services/medChronServiceV2.types";

import { extractDate } from "../../../../utils";
import type { CaseFileOption, EditingHeaderValues, MedChronTemplate, VisitDisplay } from "../types";
import { toCitationDraftRows } from "./visitCitations";

export const shortenFileName = (fileName: string): string =>
  fileName.length <= 40 ? fileName : `${fileName.slice(0, 20)}...${fileName.slice(-17)}`;

export const buildVisitTypeOptions = (template: MedChronTemplate | null): SelectOption[] =>
  Object.entries(template?.visitTypes ?? {}).map(([typeId, typeData]) => ({
    value: typeId,
    label: typeData.name,
  }));

/** Labels each file from a fileId → display-name mapping. */
export const buildFileOptions = (
  files: CaseFileOption[],
  fileMapping: Record<string, string>
): SelectOption[] =>
  files.map((file) => ({
    value: file.file_id,
    label: shortenFileName(fileMapping[file.file_id] ?? "Unknown file"),
  }));

export const formatVisitType = (template: MedChronTemplate | null, visitTypeId: string): string => {
  if (!template?.visitTypes) return "Loading...";
  const vt = template.visitTypes[visitTypeId];
  return vt ? vt.name : "Unknown Visit Type";
};

export const formatDetailCategory = (
  template: MedChronTemplate | null,
  category: string,
  isCollapsed = false
): string => {
  const withSpaces = category.replace(/-/g, " ");
  if (!template?.visitTypes) return isCollapsed ? `Sample ${withSpaces}` : withSpaces;
  for (const vt of Object.values(template.visitTypes)) {
    if (vt.details?.[category]) {
      const name = vt.details[category].name;
      return isCollapsed ? `Sample ${name}` : name;
    }
  }
  const formatted = withSpaces
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return isCollapsed ? `Sample ${formatted}` : formatted;
};

/** Seeds header-edit form state from a visit's current values. */
export const buildInitialHeaderValues = (visit: VisitDisplay): EditingHeaderValues => ({
  visit_date: extractDate(visit.visit_date) ?? "",
  visit_type_id: visit.visit_type_id,
  provider_name: visit.provider_name ?? "",
  facility_name: visit.facility_name ?? "",
  visit_subtype: visit.visit_subtype ?? "",
  citations: toCitationDraftRows(visit.citations),
});

export const formatPageRanges = (pages: number[] | undefined | null): string => {
  if (!pages || pages.length === 0) return "N/A";

  const sortedPages = Array.from(new Set(pages)).sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sortedPages[0];
  let end = sortedPages[0];

  for (let i = 1; i < sortedPages.length; i++) {
    if (sortedPages[i] === end + 1) {
      end = sortedPages[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = sortedPages[i];
      end = sortedPages[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);

  return ranges.join(", ");
};

export const formatCitationPages = (citation: MedVisitCitation): string =>
  citation.start_page === citation.end_page
    ? `p. ${citation.start_page}`
    : `pp. ${citation.start_page}-${citation.end_page}`;

export const getCitationFileLabel = (
  citation: MedVisitCitation,
  fileMapping: Record<string, string>
): string => shortenFileName(fileMapping[citation.file_id] ?? "Unknown file");

export const formatCitationLabel = (
  citation: MedVisitCitation,
  fileMapping: Record<string, string>
): string => `${getCitationFileLabel(citation, fileMapping)} ${formatCitationPages(citation)}`;
